import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Skeleton } from '@blade/ui-kit'
import { Numeric } from '@blade/ui-kit'
import { MathUtil } from '@root/shared'
import { formatCount } from '@root/shared/core/NumberFormat'
import { useOptionalAtom } from '@root/shared/lib'

import { METRICS_COLOR_MAP, type MetricsTheme } from './constants'
import { TradingPairSummaryRow } from './trading-pair-metrics-summary-row'
import { TradingPairSummaryRowSkeleton } from './trading-pair-metrics-summary-row-skeleton'

import type { PairPeriodStatsModel } from '@root/entities/pair/atoms/reatom-pair-metrics'

type TradingPairMetricsProps = {
  metrics: PairPeriodStatsModel | undefined
  metricsTheme: MetricsTheme
}

export const TradingPairMetricsPanelSummary: React.FC<
  TradingPairMetricsProps
> = ({ metrics, metricsTheme }) => {
  const { t } = useTranslation()

  // const spanMetrics = metrics?.buySellMetrics[interval]
  const white = METRICS_COLOR_MAP[metricsTheme]['white']

  const transactions = useOptionalAtom(metrics?.transactions)
  const makers = useOptionalAtom(metrics?.makers)
  const buys = useOptionalAtom(metrics?.buys)
  const sells = useOptionalAtom(metrics?.sells)
  const buyers = useOptionalAtom(metrics?.buyers)
  const sellers = useOptionalAtom(metrics?.sellers)
  const buyVolumeUsd = useOptionalAtom(metrics?.buyVolumeUsd)
  const sellVolumeUsd = useOptionalAtom(metrics?.sellVolumeUsd)
  const volumeUsd = useOptionalAtom(metrics?.volumeUsd)

  return (
    <Root>
      <Side>
        <Line>
          <Title>{t('trading.pair.tx')}</Title>
          <Value $white={white}>
            {transactions === undefined ? (
              <Skeleton width={40} height={20} paddingBlock={3} />
            ) : (
              formatCount(transactions)
            )}
          </Value>
        </Line>
        <Line>
          <Title>{t('trading.pair.volume')}</Title>
          <Value $white={white}>
            {volumeUsd === undefined ? (
              <Skeleton width={30} height={20} paddingBlock={3} />
            ) : (
              <Numeric
                value={volumeUsd}
                preset="currency-amount-short"
                currency="$"
              />
            )}
          </Value>
        </Line>
        <Line>
          <Title>{t('trading.pair.makers')}</Title>
          <Value $white={white}>
            {makers === undefined ? (
              <Skeleton width={50} height={20} paddingBlock={3} />
            ) : (
              formatCount(makers)
            )}
          </Value>
        </Line>
      </Side>
      <Summary>
        {buys !== undefined &&
        sells !== undefined &&
        buyVolumeUsd !== undefined &&
        sellVolumeUsd !== undefined &&
        buyers !== undefined &&
        sellers !== undefined ? (
          <>
            <TradingPairSummaryRow
              metricsTheme={metricsTheme}
              startLabel={t('trading.pair.buys')}
              endLabel={t('trading.pair.sells')}
              startValue={formatCount(buys)}
              endValue={formatCount(sells)}
              startPercents={MathUtil.proportion(buys, sells)}
            />
            <TradingPairSummaryRow
              metricsTheme={metricsTheme}
              startLabel={t('trading.pair.buy_volume')}
              endLabel={t('trading.pair.sell_volume')}
              startValue={
                <>
                  <Numeric
                    value={buyVolumeUsd}
                    preset="currency-amount-short"
                    currency="$"
                  />
                </>
              }
              endValue={
                <>
                  <Numeric
                    value={sellVolumeUsd}
                    preset="currency-amount-short"
                    currency="$"
                  />
                </>
              }
              startPercents={MathUtil.proportion(
                buyVolumeUsd.toNumber(),
                sellVolumeUsd.toNumber(),
              )}
            />
            <TradingPairSummaryRow
              metricsTheme={metricsTheme}
              startLabel={t('trading.pair.buyers')}
              endLabel={t('trading.pair.sellers')}
              startValue={formatCount(buyers)}
              endValue={formatCount(sellers)}
              startPercents={MathUtil.proportion(buyers, sellers)}
            />
          </>
        ) : (
          <>
            <TradingPairSummaryRowSkeleton />
            <TradingPairSummaryRowSkeleton />
            <TradingPairSummaryRowSkeleton />
          </>
        )}
      </Summary>
    </Root>
  )
}

const Root = styled.section`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  padding-inline: 4px;
`

const Line = styled.div`
  height: 50px;
`

const Title = styled.div`
  color: var(--text-9);

  font-size: 10px;
  font-weight: 600;
  line-height: 12px;
`

const Value = styled.div<{ $white: string }>`
  color: ${({ $white }) => $white};
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
`

const Side = styled.aside`
  padding-top: 5px;
  min-width: 80px;
  display: grid;
  gap: 10px;
`

const Summary = styled.summary`
  border-left: 1px solid ${({ theme }) => theme.color.white[10]};

  display: grid;

  padding-inline-start: 15px;
  padding-top: 5px;
  gap: 10px;
`
